<template>
    <div class="annual-summary-block-table">
        <CustomLoader v-if="!Object.keys(activeBlock).length" />
        <draggable v-else v-model="blockTables" handle=".annual-summary-block-table-drag-header">
            <div v-for="table in blockTables" :key="`table_${table.id}`" class="annual-summary-block-table-drag">
                <div class="annual-summary-block-table-drag-header">
                    <div class="annual-summary-block-table-drag-header__handle"></div>
                    <div class="annual-summary-block-table-drag-header__title" @click="handleTableExpand(table.id)">
                        <div class="annual-summary-block-table-drag-header__title__with-formula">
                            <span>{{ `${formatTableId(table.id)} ${table.title}` }}</span>
                            <span class="annual-summary-block-table-drag-header__title__formula">{{
                                `${getSpecificName(table) || ''}`
                            }}</span>
                        </div>
                        <span class="annual-summary-block-table-drag-header__subtitle">{{
                            `${table.subtitle || ''}`
                        }}</span>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon
                        @click="handleTableExpand(table.id)"
                        :class="{ 'mdi-rotate-180': expandedTables.includes(table.id) }"
                    >
                        mdi-arrow-up-drop-circle-outline
                    </v-icon>
                </div>
                <div class="annual-summary-block-table-drag-content" v-if="expandedTables.includes(table.id)">
                    <AnnualSummaryBlockTableRowItemsComponent
                        :table="table"
                        @save="handleSaveClick"
                        @delete="handleDeleteClick"
                        @recalculate="handleRecalculationClick"
                    />
                </div>
            </div>
        </draggable>

        <div class="d-flex mt-3 annual-summary-block-table-footer justify-end">
            <v-btn @click="handleAddTableClick">+</v-btn>
        </div>
    </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import draggable from 'vuedraggable'
import CustomLoader from '../CustomLoader.vue'
import AnnualSummaryBlockTableRowItemsComponent from './AnnualSummaryBlockTableRowItemsComponent.vue'
import moment from 'moment'

export default {
    name: 'AnnualSummaryBlockTableComponent',
    data() {
        return {
            drag: false,
            expandedTables: [],
        }
    },
    components: {
        AnnualSummaryBlockTableRowItemsComponent,
        CustomLoader,
        draggable,
    },
    props: {
        blockId: {
            default: null,
            type: Number,
        },
    },
    async mounted() {
        await this.fetchBlockTables({ blockId: this.blockId })
    },
    beforeDestroy() {
        this.resetActiveBlock()
    },
    computed: {
        ...mapState('annualSummary', ['activeBlock']),
        blockTables: {
            get() {
                return this.activeBlock.tables || []
            },
            async set(newValue) {
                this.setActiveBlockTables(newValue)
                await this.saveBlockTableSortOrder({ blockId: this.blockId })
            },
        },
    },
    methods: {
        ...mapActions('annualSummary', [
            'fetchBlockTables',
            'saveBlockTable',
            'saveBlockTableSortOrder',
            'deleteBlockTable',
            'recalculateAnnualSummary',
        ]),
        ...mapMutations('annualSummary', [
            'resetActiveBlock',
            'setActiveBlockTables',
            'addNewTableRow',
            'addNewBlockTableRow',
        ]),
        handleTableExpand(tableId) {
            if (this.expandedTables.includes(tableId)) {
                this.expandedTables = this.expandedTables.filter(i => i !== tableId)
            } else {
                this.expandedTables.push(tableId)
            }
        },

        async handleSaveClick(tableId) {
            const response = await this.saveBlockTable({ blockId: this.blockId, tableId })

            if (response) {
                await this.fetchBlockTables({ blockId: this.blockId })
            }
        },

        async handleDeleteClick(tableId) {
            const response = await this.deleteBlockTable({ blockId: this.blockId, tableId })

            if (response) {
                await this.fetchBlockTables({ blockId: this.blockId })
            }
        },

        async handleRecalculationClick(tableId) {
            await this.recalculateAnnualSummary({ tableId })
        },

        handleAddTableClick() {
            this.addNewBlockTableRow()
            const lastTable = this.blockTables[this.blockTables.length - 1]
            this.handleTableExpand(lastTable.id)
        },
        formatTableId(id) {
            if (id.toString().includes('x')) {
                return 'New (unsaved) - '
            }

            return ''
        },
        getSpecificName(table) {
            let str = ''

            if (table.formula) {
                str = table.formula
            }

            if (table.start_date) {
                let endDate = '...'

                if (table.end_date) {
                    endDate = this.formatDate(table.end_date)
                }

                str += `  -  ${this.formatDate(table.start_date)}-${endDate}`
            }

            return str
        },
        formatDate(date) {
            if (date) {
                return moment(date).format('DD.MM.YY')
            }
            return '-'
        },
    },
}
</script>

<style scoped lang="scss">
.annual-summary-block-table {
    padding: 8px;

    &-footer {
        gap: 16px;
    }

    &__draggable {
        display: flex;
        flex-direction: column;
        user-select: none;
        background: red;
    }

    &-drag {
        display: flex;
        flex-direction: column;
        border-bottom: 1px solid gray;

        &-header {
            padding: 16px 8px;
            display: flex;
            align-items: center;
            gap: 16px;

            &__title {
                display: flex;
                flex-direction: column;
                cursor: pointer;
                flex: 1;

                &__with-formula {
                    display: flex;
                    gap: 16px;
                    align-items: baseline;
                }
                &__formula {
                    color: gray;
                    font-size: 12px;
                    font-style: italic;
                }
            }

            &__subtitle {
                color: gray;
                font-size: 12px;
            }

            &__handle {
                width: 24px;
                height: 8px;
                border-bottom: 2px solid gray;
                border-top: 2px solid gray;
                cursor: pointer;
            }
        }

        &-content {
            margin: 0 16px;
            padding: 16px 0;
            border-top: 1px solid lightgray;
        }
    }
}
</style>
